import posthog from 'posthog-js'

import type { BeforeInstallPromptEvent } from '@app/types'
import { PosthogEvent } from '@app/constants/analytics'

export type PwaPlatform = 'android' | 'ios' | 'desktop'
export type PwaDisplayMode = 'standalone' | 'browser'
export type PushPermissionState = NotificationPermission | 'unsupported'
export type AppInstallMethod = 'prompt' | 'inferred'

const APP_INSTALLED_FLAG_KEY = 'vb_ph_app_installed_tracked'
const FIRST_VISIT_FLAG_KEY = 'vb_ph_first_visit_tracked'
const UTM_KEYS = [ 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content' ] as const

let initialized = false
let promptSource: string | null = null
let lastSyncedContext: string | null = null

const safeGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

const safeSet = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value)
  } catch {
    // storage unavailable (private mode) — tracking may repeat, but must never break the app
  }
}

const tenantGroups = () => ({ tenant: window.location.hostname })

const capture = (event: string, properties: Record<string, unknown> = {}): void => {
  posthog.capture(event, { ...properties, $groups: tenantGroups() })
}

export const getPlatform = (): PwaPlatform => {
  const ua = window.navigator.userAgent || ''
  // iPadOS 13+ reports itself as Macintosh; touch support tells it apart from a real Mac
  const isIPadOS = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1
  if (/iPhone|iPad|iPod/i.test(ua) || isIPadOS) return 'ios'
  if (/Android/i.test(ua)) return 'android'
  return 'desktop'
}

export const getBrowser = (): string => {
  const ua = window.navigator.userAgent || ''
  if (/SamsungBrowser/i.test(ua)) return 'samsung_internet'
  if (/Telegram/i.test(ua) || isTelegramWebview()) return 'telegram'
  if (/EdgA?\//i.test(ua)) return 'edge'
  if (/CriOS|Chrome/i.test(ua)) return 'chrome'
  if (/FxiOS|Firefox/i.test(ua)) return 'firefox'
  if (/Safari/i.test(ua)) return 'safari'
  return 'other'
}

export const getDisplayMode = (): PwaDisplayMode => {
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true
  const standalone = [ 'standalone', 'fullscreen', 'minimal-ui', 'window-controls-overlay' ].some(
    (mode) => window.matchMedia?.(`(display-mode: ${mode})`).matches,
  )
  return iosStandalone || standalone ? 'standalone' : 'browser'
}

export const getPushPermission = (): PushPermissionState => {
  if (typeof Notification === 'undefined' || !('permission' in Notification)) return 'unsupported'
  return Notification.permission
}

const isTelegramWebview = (): boolean => {
  const w = window as Window & { TelegramWebviewProxy?: unknown; Telegram?: { WebApp?: { initData?: string } } }
  return !!w.TelegramWebviewProxy || !!w.Telegram?.WebApp?.initData
}

const getPwaContext = () => {
  const displayMode = getDisplayMode()
  return {
    platform: getPlatform(),
    display_mode: displayMode,
    is_installed: displayMode === 'standalone',
    push_permission: getPushPermission(),
  }
}

/**
 * Sets PWA state as person properties and super properties (so every event carries it too).
 * Called on every app open and whenever display mode or push permission changes.
 */
export const syncPwaProperties = (): void => {
  const context = getPwaContext()
  lastSyncedContext = JSON.stringify(context)
  posthog.register(context)
  posthog.setPersonProperties(context)
}

/**
 * Fires `app installed` at most once per browser storage. On Android/desktop the installed app
 * shares storage with the browser, so `appinstalled` + prompt acceptance + Samsung re-fires all
 * collapse into one event. iOS standalone apps get fresh storage, so the first standalone launch
 * is the install.
 */
export const trackAppInstalled = (method: AppInstallMethod, source?: string): void => {
  if (safeGet(APP_INSTALLED_FLAG_KEY)) return
  safeSet(APP_INSTALLED_FLAG_KEY, new Date().toISOString())

  capture(PosthogEvent.APP_INSTALLED, {
    platform: getPlatform(),
    display_mode: getDisplayMode(),
    method,
    browser: getBrowser(),
    ...(source ? { source } : {}),
  })
  posthog.setPersonProperties({ is_installed: true }, { first_installed_platform: getPlatform() })
}

/**
 * Shows the native install prompt (Android/desktop) and tracks shown/accepted/dismissed.
 * `source` identifies the UI that triggered it, e.g. 'app_menu'.
 */
export const promptInstall = async (
  installEvent: BeforeInstallPromptEvent | null,
  source: string,
): Promise<'accepted' | 'dismissed' | null> => {
  if (!installEvent) return null

  const props = { platform: getPlatform(), browser: getBrowser(), source }
  promptSource = source
  capture(PosthogEvent.INSTALL_PROMPT_SHOWN, props)

  await installEvent.prompt()
  const { outcome } = await installEvent.userChoice

  capture(
    outcome === 'accepted' ? PosthogEvent.INSTALL_PROMPT_ACCEPTED : PosthogEvent.INSTALL_PROMPT_DISMISSED,
    props,
  )
  if (outcome === 'accepted') trackAppInstalled('prompt', source)

  return outcome
}

export const trackIosInstallInstructionsShown = (source: string): void => {
  capture(PosthogEvent.IOS_INSTALL_INSTRUCTIONS_SHOWN, { platform: getPlatform(), browser: getBrowser(), source })
}

/**
 * Requests notification permission and tracks the request and its result.
 */
export const requestPushPermission = async (source: string): Promise<PushPermissionState> => {
  const previous = getPushPermission()
  if (previous === 'unsupported') return previous

  capture(PosthogEvent.PUSH_PERMISSION_REQUESTED, { source, previous, platform: getPlatform() })

  let result: PushPermissionState = previous
  try {
    result = (await Notification.requestPermission()) ?? getPushPermission()
  } catch (e) {
    console.error('Error Notification Permission Request: ', e)
    result = getPushPermission()
  }

  capture(PosthogEvent.PUSH_PERMISSION_RESULT, { source, previous, result, platform: getPlatform() })
  syncPwaProperties()

  return result
}

const syncPwaPropertiesIfChanged = (): void => {
  if (JSON.stringify(getPwaContext()) !== lastSyncedContext) syncPwaProperties()
}

const getReferringDomain = (referrer: string): string | null => {
  try {
    return referrer ? new URL(referrer).hostname : null
  } catch {
    return null
  }
}

/**
 * Saves UTM params + referrer on the first visit as set-once person properties.
 * Telegram's in-app browser usually sends no referrer, so it is detected separately.
 */
const trackFirstVisit = (): void => {
  if (safeGet(FIRST_VISIT_FLAG_KEY)) return
  safeSet(FIRST_VISIT_FLAG_KEY, new Date().toISOString())

  const params = new URLSearchParams(window.location.search)
  const referrer = document.referrer || ''
  const referringDomain = getReferringDomain(referrer)
  const utm = UTM_KEYS.reduce<Record<string, string>>((acc, key) => {
    const value = params.get(key)
    if (value) acc[`first_${key}`] = value
    return acc
  }, {})

  const fromTelegram =
    isTelegramWebview() ||
    /Telegram/i.test(window.navigator.userAgent) ||
    /(^|\.)(t\.me|telegram\.org|telegram\.me)$/i.test(referringDomain ?? '') ||
    /telegram/i.test(params.get('utm_source') ?? '')

  posthog.setPersonProperties(
    {},
    {
      ...utm,
      first_referrer: referrer || '$direct',
      first_referring_domain: referringDomain ?? '$direct',
      first_landing_path: window.location.pathname,
      first_visit_platform: getPlatform(),
      first_visit_from_telegram: fromTelegram,
    },
  )
}

/**
 * Registers PWA listeners once. Must run outside React (StrictMode double-mounts effects,
 * which is how duplicate listeners sneak in).
 */
export const initPwaTracking = (): void => {
  if (initialized || typeof window === 'undefined') return
  initialized = true

  trackFirstVisit()
  syncPwaProperties()

  if (getPlatform() === 'ios' && getDisplayMode() === 'standalone') {
    trackAppInstalled('inferred')
  }

  window.addEventListener('appinstalled', () => {
    trackAppInstalled('prompt', promptSource ?? 'browser_ui')
    syncPwaProperties()
  })

  window.matchMedia?.('(display-mode: standalone)').addEventListener?.('change', syncPwaPropertiesIfChanged)

  navigator.permissions
    ?.query({ name: 'notifications' as PermissionName })
    .then((status) => {
      status.addEventListener('change', syncPwaPropertiesIfChanged)
    })
    .catch(() => undefined)

  document.addEventListener('visibilitychange', () => {
    // installed apps are usually resumed, not relaunched — catch permission changes made in OS settings
    if (document.visibilityState === 'visible') syncPwaPropertiesIfChanged()
  })
}
